import IntlMessageFormat from "intl-messageformat";
import en from "./locales/en";
import fr from "./locales/fr";
import type { Locale } from "./types";

const messages: Record<Locale, Record<string, string>> = { en, fr };

function readLocalStorage(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(key);
  } catch (err) {
    console.warn("[i18n] read locale from storage failed", { key }, err);
    return null;
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    // SSR or private-browsing environments
    console.warn("[i18n] write locale to storage failed", { key }, err);
  }
}

/**
 * A plain external store — deliberately not a MobX observable.
 *
 * `useTranslation` is used by ~600 components, most of them not wrapped in
 * `observer`, and both the hook and `t()` read the current locale. As a MobX
 * observable that made this the largest single source of the dev-only
 * "[mobx] Observable ... being read outside a reactive context" warning
 * (`observableRequiresReaction`, enabled in `stores/store-context.tsx`).
 * Consumers subscribe through `useSyncExternalStore` instead, which is reactive
 * for observer and non-observer components alike.
 */
class TranslationStore {
  private locale: Locale = "en";
  private formatterCache = new Map<string, IntlMessageFormat>();
  private listeners = new Set<() => void>();
  /**
   * Per-locale replacements consulted ahead of the locale map, so a flagged
   * terminology change needs no conditional at any `t()` call site. Null when no
   * overlay applies, which is the shipped default. See
   * `locales/system-role-vocabulary.ts` and `SystemRoleVocabularySync`.
   */
  private vocabularyOverlay: Record<Locale, Record<string, string>> | null = null;

  get currentLocale(): Locale {
    return this.locale;
  }

  /**
   * Arrow property, not a method: `useSyncExternalStore` re-subscribes whenever
   * the `subscribe` reference changes, so it has to be stable per store.
   */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emitChange(): void {
    for (const listener of this.listeners) listener();
  }

  hydratePersistedLocale(): void {
    const persisted = readLocalStorage("locale");
    if (persisted !== "en" && persisted !== "fr") return;
    const changed = persisted !== this.locale;
    this.locale = persisted;
    this.formatterCache.clear();
    if (changed) this.emitChange();
  }

  setLocale(locale: Locale): void {
    this.locale = locale;
    this.formatterCache.clear();
    writeLocalStorage("locale", locale);
    this.emitChange();
  }

  /**
   * Swap in (or drop, with `null`) a set of per-locale replacements that win
   * over the locale files. Clearing the formatter cache is not optional: it is
   * keyed on `locale:key`, so a cached formatter built from the old string would
   * keep rendering it for every key that takes `values`.
   *
   * No-ops when the overlay is already the one asked for, so the flag bridge can
   * call it on every render without emitting a change to ~600 consumers.
   */
  setVocabularyOverlay(overlay: Record<Locale, Record<string, string>> | null): void {
    if (this.vocabularyOverlay === overlay) return;
    this.vocabularyOverlay = overlay;
    this.formatterCache.clear();
    this.emitChange();
  }

  t(key: string, values?: Record<string, unknown>, fallback?: string): string {
    const localeMessages = messages[this.locale];
    const fallbackMessages = messages["en"];
    const overlay = this.vocabularyOverlay;

    // The overlay is consulted per locale with no cross-locale fallback: a key
    // it translates in `en` but not `fr` should render the shipped French
    // string, not the English override.
    const raw = overlay?.[this.locale]?.[key] ?? localeMessages[key] ?? fallbackMessages[key];
    if (raw === undefined) {
      return fallback ?? key;
    }

    if (values !== undefined) {
      try {
        const cacheKey = `${this.locale}:${key}`;
        let formatter = this.formatterCache.get(cacheKey);
        if (!formatter) {
          formatter = new IntlMessageFormat(raw, this.locale);
          this.formatterCache.set(cacheKey, formatter);
        }
        return String(formatter.format(values));
      } catch {
        return raw;
      }
    }

    return raw;
  }
}

export const translationStore = new TranslationStore();
export { TranslationStore };
